import React, { useState } from 'react';
import { useLocale } from '../../contexts/LocaleContext';
import { UserRole } from '../../contexts/AuthContext';
import './SharedComponents.css';

interface AddUserFormProps {
  onSubmit: (email: string, name: string, role: UserRole, orgId?: string) => Promise<void>;
  onCancel: () => void;
  organizations?: { id: string; name: string }[];
  showOrgSelector?: boolean;
  defaultOrgId?: string;
  isAdmin?: boolean;
  availableRoles?: UserRole[];
  isLoading?: boolean;
}

export const AddUserForm: React.FC<AddUserFormProps> = ({
  onSubmit,
  onCancel,
  organizations = [],
  showOrgSelector = false,
  defaultOrgId = '',
  isAdmin = false,
  availableRoles = ['org_user', 'org_admin', 'viewer'],
  isLoading = false,
}) => {
  const { t } = useLocale();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('org_user');
  const [orgId, setOrgId] = useState(defaultOrgId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;
    if (showOrgSelector && !orgId) return;

    await onSubmit(email.trim(), name.trim(), role, orgId || undefined);
    setEmail('');
    setName('');
    setRole('org_user');
  };

  const getRoleLabel = (r: UserRole) => {
    switch (r) {
      case 'viewer': return t('admin.roleViewer');
      case 'org_user': return t('admin.roleUser');
      case 'org_admin': return t('admin.roleOrgAdmin');
      case 'admin': return t('admin.rolePlatformAdmin');
      default: return r;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="inline-form">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('admin.userName')}
        required
        autoFocus
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('admin.userEmail')}
        required
      />
      {showOrgSelector && (
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          required
        >
          <option value="">{t('admin.selectOrg')}</option>
          {organizations.map(org => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      )}
      <select
        value={role}
        onChange={(e) => setRole(e.target.value as UserRole)}
      >
        {availableRoles.map(r => (
          <option key={r} value={r}>{getRoleLabel(r)}</option>
        ))}
        {isAdmin && !availableRoles.includes('admin') && (
          <option value="admin">{t('admin.rolePlatformAdmin')}</option>
        )}
      </select>
      <button type="submit" className="btn-primary" disabled={isLoading}>
        {isLoading ? t('general.loading') : t('admin.addUser')}
      </button>
      <button type="button" className="btn-secondary" onClick={onCancel}>
        {t('general.cancel')}
      </button>
    </form>
  );
};

export default AddUserForm;
